import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";

interface EmailFramePanelProps {
  to: string;
  subject: string;
  bodyPreview: string;
  onToChange?: (to: string) => void;
  onSubjectChange?: (subject: string) => void;
  onSend?: () => void;
  onSaveDraft?: () => void;
  onClose?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function EmailFramePanel({
  to,
  subject,
  bodyPreview,
  onToChange,
  onSubjectChange,
  onSend,
  onSaveDraft,
  onClose,
  className,
  "data-testid": testId = "email-frame-panel",
}: EmailFramePanelProps) {
  return (
    <aside
      className={cn(
        "flex w-64 flex-col rounded-2xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm",
        className,
      )}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex h-11 items-center gap-2 border-b border-glass-stroke-subtle px-3.5">
        <Icons.Mail className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
        <span className="text-xs font-semibold text-primary">Email</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center rounded-md p-0.5 text-muted transition-colors hover:text-primary"
          data-testid="email-frame-panel-close"
          aria-label="Close panel"
        >
          <Icons.X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Form fields */}
      <div className="flex flex-col gap-3 p-3.5">
        {/* To field */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[10px] font-medium text-muted"
            htmlFor="email-panel-to"
          >
            To
          </label>
          <TextInput
            id="email-panel-to"
            value={to}
            onChange={(e) => onToChange?.(e.target.value)}
            placeholder="recipient@example.com"
            data-testid="email-frame-panel-to"
          />
        </div>

        {/* Subject field */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[10px] font-medium text-muted"
            htmlFor="email-panel-subject"
          >
            Subject
          </label>
          <TextInput
            id="email-panel-subject"
            value={subject}
            onChange={(e) => onSubjectChange?.(e.target.value)}
            placeholder="Email subject"
            data-testid="email-frame-panel-subject"
          />
        </div>

        {/* Body preview */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-muted">Body</span>
            <button
              type="button"
              className="text-[10px] text-accent transition-colors hover:brightness-110"
              data-testid="email-frame-panel-edit-body"
            >
              Edit body
            </button>
          </div>
          <div
            className="line-clamp-4 rounded-lg border border-glass-stroke-subtle bg-glass-fill-heavy px-3 py-2 text-[10px] leading-relaxed text-secondary"
            data-testid="email-frame-panel-body-preview"
          >
            {bodyPreview || (
              <span className="italic text-muted">No body content</span>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center gap-2 border-t border-glass-stroke-subtle px-3.5 py-2.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSaveDraft}
          data-testid="email-frame-panel-save-draft"
        >
          Save Draft
        </Button>
        <div className="flex-1" />
        <Button
          variant="default"
          size="sm"
          onClick={onSend}
          disabled={!to || !subject}
          data-testid="email-frame-panel-send"
        >
          <Icons.Send className="h-3 w-3" />
          Send
        </Button>
      </div>
    </aside>
  );
}

export type { EmailFramePanelProps };
